import { Injectable } from '@angular/core';
import { CacheService } from "./cache.service";
import { BillingAddress, RawProfile } from '../interfaces/user-profile';
import { BehaviorSubject, Subject } from 'rxjs';
import sdk from "@sp400/sdk";
import { SdkService } from './sdk.service';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {

  private _deletingCardInfo: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public deletingCardInfo$ = this._deletingCardInfo.asObservable();

  private _securityQuestions: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public securityQuestions$ = this._securityQuestions.asObservable();

  private _newPaymentDetails: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public newPaymentDetails$ = this._newPaymentDetails.asObservable();

  private dataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public data$ = this.dataSubject.asObservable();

  private timerDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public timerData$ = this.timerDataSubject.asObservable();

  private pinDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public pinData$ = this.pinDataSubject.asObservable();

  private _newPaymentMethodSuccess: Subject<any> = new Subject<any>();
  public newPaymentMethodAdded$ = this._newPaymentMethodSuccess.asObservable();

  private _deletePaymentMethodCompleted: Subject<any> = new Subject<any>();
  public deletePaymentMethodCompleted$ = this._deletePaymentMethodCompleted.asObservable();

  private _resendOtp: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public resendOtp$ = this._resendOtp.asObservable();

  private secondaryConatctNameSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public secondaryConatctNameSubject$ = this.secondaryConatctNameSubject.asObservable();

  private _successModalMsgType: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public successModalMsgType$ = this._successModalMsgType.asObservable();

  private secondaryConatctDeleteSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public secondaryConatctDelete$ = this.secondaryConatctDeleteSubject.asObservable();

  constructor(
    private cacheService: CacheService,
    private sdkService: SdkService
  ) {
  }

  getProfileData(): Promise<any> {
    return this.cacheService.get('authentication.profile', null)
  }

  getProfileBillingAddress(billingInfo: any) {
    if (Object.keys(billingInfo).length) {
      let compiledAddress = Object.keys(billingInfo).map((info, index) => {
        if (index == 0) {
          return `${billingInfo[info]}`;
        }
        return ` ${billingInfo[info]}`;
      }).join();
      compiledAddress = compiledAddress.slice(0, compiledAddress.indexOf(',')) + compiledAddress.slice(compiledAddress.indexOf(',') + 1);
      return compiledAddress;
    } else {
      return '-';
    }
  }

  setDeletingCardInfo(paymentInfo: any) {
    this._deletingCardInfo.next(paymentInfo);
  }

  setSecurityQuestion(questions: any) {
    this._securityQuestions.next(questions);
  }

  setAddNewPaymentDetails(details: any) {
    this._newPaymentDetails.next(details);
  }

  sendDataToDisableTimerModal(data: any): void {
    this.dataSubject.next(data);
  }

  setDataFromDisableTimerModal(data: any): void {
    this.timerDataSubject.next(data);
  }

  setDataFromPinModal(data: any): void {
    this.pinDataSubject.next(data);
  }

  setSuccessModalMessageType(type: 'Security_PIN' | 'Security_QA'): void {
    this._successModalMsgType.next(type);
  }

  getContactsPreferences(vin: any) {
    return this.cacheService.get('sdpr.getContactsPreferences', { vin: vin });
  }

  getPrimaryContactPReferences(contactsPreferencesList: any[]) {
    let contactsFilteredByEmail = [];
    let contactsFilteredBySms = [];
    if (contactsPreferencesList) {
      const contactsFilterBySelf = contactsPreferencesList.filter(contact => {
        return contact.contactId === 'self';
      });
      if (contactsFilterBySelf.length > 0) {
        contactsFilteredByEmail = contactsFilterBySelf.filter(contact => {
          return contact.contactType === 'email';
        });
      }

      if (contactsFilteredByEmail.length === 0) {
        contactsFilteredBySms = contactsFilterBySelf.filter(contact => {
          return contact.contactType === 'sms';
        });
      } else {
        return contactsFilteredByEmail[0];
      }

      if (contactsFilteredBySms.length === 0) {
        return null;
      } else {
        return contactsFilteredBySms[0];
      }
    }

  }

  postContactsPreferences(payload: any) {
    return this.sdkService.set('sdpr.postContactsPreferences', payload);
  }

  getSecondaryContactsPreferencesList(contactsPreferencesList: any) {
    contactsPreferencesList = contactsPreferencesList.length ? contactsPreferencesList : [];
    const secondaryContacts = contactsPreferencesList?.filter((contact: { contactId: string; }) => {
      return contact.contactId !== 'self';
    }) || [];

    return secondaryContacts.splice(0, 4);
  }

  getContacts(vin: string) {
    const payload = { vin: vin };
    return this.cacheService.get('sdpr.getContacts', payload);
  }

  postContacts(vin: string, contacts: any) {
    const payload = { vin: vin, contacts: contacts };
    // return this.cacheService.set('sdpr.postContacts', payload);
    return this.sdkService.set('sdpr.postContacts', payload);
  }

  putContacts(vin: string, contacts: any) {
    const payload = { vin: vin, contacts };
    return this.sdkService.set('sdpr.putContacts', payload);
  }

  deleteContacts(vin: string, contact: any) {
    const payload = { vin: vin, contact };
    return this.cacheService.set('sdpr.deleteContacts', payload);
  }

  getTheftStatus(vin: string) {
    return this.cacheService.get('sdpr.theftStatus', { vin: vin });
  }

  postTheftStatus(payload: object) {
    return this.cacheService.set('sdpr.theftStatus', payload);
  }

  getAuthenticatePin(pin: string) {
    return this.cacheService.set('fcl.authenticatePin', { pin: pin });
  }

  setAddNewPaymentMethodSuccess(success: boolean) {
    this._newPaymentMethodSuccess.next(success)
  }

  setDeletePaymentStatus(completeStatus: boolean) {
    this._deletePaymentMethodCompleted.next({ status: completeStatus });
  }

  resendOtp() {
    this._resendOtp.next(true);
  }

  setSecondaryConatctName(data: any) {
    this.secondaryConatctNameSubject.next(data);
  }

  setSecondaryConatctDeleteData(data: any) {
    this.secondaryConatctDeleteSubject.next(data);
  }
}